import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { MetaOptionList, SwatchPicker } from '@/components/ai-tools/option-pickers';
import { EMPTY_PHOTO_SLOT, PhotoPicker, type PhotoSlot } from '@/components/ai-tools/photo-picker';
import { RenderGrid, type RenderGridItem } from '@/components/ai-tools/render-grid';
import { StickyActionBar } from '@/components/ai-tools/sticky-action-bar';
import { StepTitle } from '@/components/ai-tools/step-title';
import { TopicInput } from '@/components/ai-tools/topic-input';
import { Button } from '@/components/ui/button';
import { localized, type DuoVideoTool } from '@/config/ai-tools';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useInView } from '@/hooks/use-in-view';
import { useRenderOutcomeTracking } from '@/hooks/use-render-outcome-tracking';
import { useStudioResume } from '@/hooks/use-studio-resume';
import { useAddStartedTask, useToolTasks } from '@/hooks/use-tool-tasks';
import { formatUsd } from '@/lib/ai-tools/format';
import { ENTRY_PACK, uploadToolPhoto } from '@/lib/ai-tools/studio';
import type { PublicToolTask } from '@/lib/ai-tools/types';
import { ApiError, apiPost } from '@/lib/api-client';
import { track } from '@/lib/track';
import type { SlotEntry } from '@/lib/store/recommendation-slot';
import type { ToolSummary } from '@/modules/directory/types';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { RecommendationStrip } from '@/blocks/store/recommendation-slot';
import { StudioPacks } from './studio-packs';
import { resolveToolCopy } from './tool-copy';

/** The form as it is parked across the Stripe round trip. */
type StudioDraft = { photoUrls: [string, string]; stageId: string; qualityId: string; topic: string };

const errorCopy: Record<string, () => string> = {
  generator_unavailable: () => m['directory.ai_tools.error.generator_unavailable'](),
  too_many_renders: () => m['directory.ai_tools.error.too_many_renders'](),
  invalid_photo: () => m['directory.ai_tools.error.invalid_photo'](),
  two_photos_required: () => m['directory.ai_tools.studio.need_photos'](),
};

/**
 * The generator panel: two selfies, a stage, a quality tier and an optional
 * topic → one credit-charged render. Owns state and API calls; every piece
 * of UI is a props-only component. Signed-out visitors see a sign-in
 * prompt up front; on phones a sticky bar repeats the generate button while
 * the main one is off screen.
 */
export function DuoVideoStudio({
  tool,
  recommendations = [],
  onNeedCredits,
}: {
  tool: DuoVideoTool;
  /** Trend-page slot entries; shown under the renders once one finishes. */
  recommendations?: SlotEntry<ToolSummary>[];
  onNeedCredits: () => void;
}) {
  const locale = getLocale();
  const t = (value: Parameters<typeof localized>[0]) => localized(value, locale);
  const copy = resolveToolCopy(tool, locale);
  const { isAuthenticated, openGate, gate } = useAuthGate('generate');
  const [photos, setPhotos] = useState<[PhotoSlot, PhotoSlot]>([EMPTY_PHOTO_SLOT, EMPTY_PHOTO_SLOT]);
  const [stageId, setStageId] = useState(tool.stages[0]?.id ?? '');
  const [qualityId, setQualityId] = useState(tool.qualities[0]?.id ?? '');
  const [topic, setTopic] = useState('');
  // Set when Generate was pressed signed out: runs once the sign-in dialog succeeds.
  const runAfterSignIn = useRef(false);

  const quality = tool.qualities.find((item) => item.id === qualityId) ?? tool.qualities[0];
  const tasksQuery = useToolTasks(tool.slug, isAuthenticated);
  const addStartedTask = useAddStartedTask(tool.slug);
  const balance = tasksQuery.data?.balance;
  const [studioRef, studioInView] = useInView<HTMLElement>();
  const [actionRef, actionInView] = useInView<HTMLDivElement>();
  const perRender = ENTRY_PACK
    ? formatUsd(Math.round((quality.credits * ENTRY_PACK.priceInCents) / ENTRY_PACK.credits))
    : null;
  const lowOnCredits = balance !== undefined && balance < quality.credits;
  const standardCredits = Math.min(...tool.qualities.map((item) => item.credits));

  const setSlot = (index: 0 | 1, slot: PhotoSlot) =>
    setPhotos((current) => {
      const next = [...current] as [PhotoSlot, PhotoSlot];
      next[index] = slot;
      return next;
    });

  const pickPhoto = async (index: 0 | 1, file: File) => {
    const preview = URL.createObjectURL(file);
    // Signed out: keep the file in memory; it uploads once the visitor signs in.
    if (!isAuthenticated) {
      setSlot(index, { status: 'local', preview, file });
      return;
    }
    setSlot(index, { status: 'uploading', preview });
    try {
      setSlot(index, { status: 'ready', preview, url: await uploadToolPhoto(file) });
    } catch {
      setSlot(index, { status: 'error', preview });
    }
  };

  /** Uploads photos picked before sign-in; resolves to the settled slots. */
  const settlePhotos = async (): Promise<[PhotoSlot, PhotoSlot]> => {
    const settled = await Promise.all(
      photos.map(async (slot): Promise<PhotoSlot> => {
        if (slot.status !== 'local' || !slot.file) return slot;
        try {
          return { status: 'ready', preview: slot.preview, url: await uploadToolPhoto(slot.file) };
        } catch {
          return { status: 'error', preview: slot.preview };
        }
      }),
    );
    const next: [PhotoSlot, PhotoSlot] = [settled[0], settled[1]];
    setPhotos(next);
    return next;
  };

  const generate = useMutation({
    mutationFn: async () => {
      const settled = await settlePhotos();
      if (!settled.every((slot) => slot.status === 'ready' && slot.url)) throw new Error('upload_failed');
      return apiPost<PublicToolTask>(`/api/ai-tools/${tool.slug}/generate`, {
        stage: stageId,
        quality: quality.id,
        photos: settled.map((slot) => slot.url),
        topic,
        locale,
      });
    },
    onSuccess: (task) => {
      toast.success(m['directory.ai_tools.studio.started']());
      addStartedTask(task);
      track('tool_generate_started', { tool: tool.slug, quality: quality.id, credits: quality.credits });
    },
    onError: (error: Error) => {
      const code = error instanceof ApiError ? error.message : '';
      // The balance on screen was stale: refresh it so the packs appear.
      if (code === 'insufficient_credits') return void tasksQuery.refetch();
      toast.error((errorCopy[code] ?? (() => m['directory.ai_tools.error.generic']()))());
    },
  });

  /** Signed in with both photos: check the balance first, so a short balance goes straight to the packs. */
  const run = async () => {
    const available = balance ?? (await tasksQuery.refetch()).data?.balance;
    if (available !== undefined && available < quality.credits) {
      track('tool_generate_short', { tool: tool.slug, missing: quality.credits - available });
      return;
    }
    generate.mutate();
  };

  const onGenerate = () => {
    track('tool_generate_click', { tool: tool.slug, authed: isAuthenticated, valid: photos.every((slot) => slot.status === 'local' || slot.status === 'ready') });
    if (photos.some((slot) => slot.status === 'empty')) return void toast.error(m['directory.ai_tools.studio.need_photos']());
    if (photos.some((slot) => slot.status === 'error')) return void toast.error(m['directory.ai_tools.studio.photo_failed']());
    if (photos.some((slot) => slot.status === 'uploading')) return;
    if (!isAuthenticated) {
      runAfterSignIn.current = true;
      track('tool_signin_gate', { tool: tool.slug });
      openGate();
      return;
    }
    void run();
  };

  useEffect(() => {
    if (isAuthenticated && runAfterSignIn.current) {
      runAfterSignIn.current = false;
      void run();
    }
  }, [isAuthenticated]);

  const getDraft = async (): Promise<StudioDraft | null> => {
    const [first, second] = await settlePhotos();
    if (first.status !== 'ready' || !first.url || second.status !== 'ready' || !second.url) return null;
    return { photoUrls: [first.url, second.url], stageId, qualityId: quality.id, topic };
  };

  const { resuming } = useStudioResume<StudioDraft>({
    slug: tool.slug,
    isAuthenticated,
    restore: (draft) => {
      setPhotos(draft.photoUrls.map((url): PhotoSlot => ({ status: 'ready', preview: url, url })) as [PhotoSlot, PhotoSlot]);
      setStageId(draft.stageId);
      setQualityId(draft.qualityId);
      setTopic(draft.topic);
    },
    refetchBalance: async () => (await tasksQuery.refetch()).data?.balance,
    needed: quality.credits,
    start: () => generate.mutate(),
  });

  const photoLabels = {
    add: m['directory.ai_tools.studio.photo_add'](),
    uploading: m['directory.ai_tools.studio.photo_uploading'](),
    remove: m['directory.ai_tools.studio.photo_remove'](),
    failed: m['directory.ai_tools.studio.photo_failed'](),
  };

  const renders: RenderGridItem[] = (tasksQuery.data?.tasks ?? []).map((task) => {
    const stage = tool.stages.find((item) => item.id === task.stage);
    return {
      id: task.id,
      state: task.status === 'success' ? 'ready' : task.status === 'failed' ? 'failed' : 'rendering',
      videoUrl: task.videoUrl,
      swatch: stage?.swatch,
      caption: [stage ? t(stage.label) : '', task.topic].filter(Boolean).join(' · '),
    };
  });
  useRenderOutcomeTracking(tool.slug, renders);
  // Plan §4.5: the slot appears only after a render succeeds or fails —
  // never next to the generator before the visitor has tried it.
  const hasFinishedRender = renders.some((render) => render.state !== 'rendering');

  return (
    <section
      ref={studioRef}
      id="studio"
      aria-label={m['directory.ai_tools.studio.label']()}
      className="scroll-mt-24 rounded-2xl border border-border bg-card p-5 shadow-xl shadow-black/5 sm:p-8 lg:p-10"
    >
      {gate}
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-12">
        <div className="space-y-8">
          <div>
            <StepTitle index={1}>{m['directory.ai_tools.studio.step_photos']()}</StepTitle>
            <div className="grid grid-cols-2 gap-3">
              {([0, 1] as const).map((index) => (
                <PhotoPicker
                  key={index}
                  label={index === 0 ? copy.photoFirst : copy.photoSecond}
                  labels={photoLabels}
                  slot={photos[index]}
                  blocked={false}
                  onBlocked={() => undefined}
                  onPick={(file) => void pickPhoto(index, file)}
                  onClear={() => setSlot(index, EMPTY_PHOTO_SLOT)}
                />
              ))}
            </div>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">{m['directory.ai_tools.studio.photo_hint']()}</p>
          </div>

          <div>
            <StepTitle index={2}>{copy.stepStage}</StepTitle>
            <SwatchPicker
              options={tool.stages.map((stage) => ({
                id: stage.id,
                label: t(stage.label),
                swatch: stage.swatch,
                image: stage.image,
              }))}
              value={stageId}
              onChange={setStageId}
            />
          </div>
        </div>

        <div className="space-y-8">
          <div>
            <StepTitle index={3}>{m['directory.ai_tools.studio.step_quality']()}</StepTitle>
            <MetaOptionList
              options={tool.qualities.map((item) => ({
                id: item.id,
                label: t(item.label),
                meta: m['directory.ai_tools.studio.credits']({ credits: item.credits }),
              }))}
              value={quality.id}
              onChange={setQualityId}
            />
          </div>

          <div>
            <StepTitle
              index={4}
              aside={<span className="text-xs text-muted-foreground">{m['directory.ai_tools.studio.optional']()}</span>}
            >
              {copy.stepTopic}
            </StepTitle>
            <TopicInput
              presets={tool.topicPresets.map((preset) => ({
                id: preset.id,
                emoji: preset.emoji,
                label: t(preset.label),
                topic: t(preset.topic),
              }))}
              value={topic}
              onChange={setTopic}
              maxLength={tool.topicMaxLength}
              placeholder={copy.topicPlaceholder}
              hint={copy.topicHint}
            />
          </div>

          <div ref={actionRef} className="space-y-3">
            {perRender ? (
              <div>
                <p className="text-2xl font-bold tracking-tight">
                  {m['directory.ai_tools.studio.price_headline']({ price: perRender })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {m['directory.ai_tools.studio.price_sub']({ credits: quality.credits })}
                </p>
              </div>
            ) : null}
            <Button
              size="lg"
              className="h-12 w-full text-base"
              disabled={generate.isPending || resuming}
              onClick={onGenerate}
            >
              {generate.isPending || resuming ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  {resuming ? m['directory.ai_tools.studio.resuming']() : copy.starting}
                </>
              ) : (
                <>
                  <Sparkles className="size-4" aria-hidden="true" />
                  {copy.generate(quality.credits)}
                </>
              )}
            </Button>
            {!isAuthenticated ? (
              <p className="text-xs text-muted-foreground">{m['directory.ai_tools.studio.signin_hint']()}</p>
            ) : null}
            {lowOnCredits && !resuming ? (
              <StudioPacks
                slug={tool.slug}
                missing={quality.credits - (balance ?? 0)}
                standardCredits={standardCredits}
                unit="video"
                getDraft={getDraft}
              />
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              {balance !== undefined ? (
                <button type="button" onClick={onNeedCredits} className="font-medium text-primary hover:underline">
                  {m['directory.ai_tools.studio.balance']({ credits: balance })} · {m['directory.ai_tools.studio.top_up']()}
                </button>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">{m['directory.ai_tools.studio.consent']()}</p>
          </div>
        </div>
      </div>

      {renders.length > 0 ? (
        <div className="mt-10 space-y-8">
          <RenderGrid
            title={m['directory.ai_tools.renders.title']()}
            items={renders}
            onDownload={() => track('tool_download', { tool: tool.slug })}
            labels={{
              rendering: m['directory.ai_tools.renders.rendering'](),
              failed: m['directory.ai_tools.renders.failed'](),
              download: m['directory.ai_tools.renders.download'](),
              waitHint: m['directory.ai_tools.renders.wait_hint'](),
            }}
          />
          {hasFinishedRender ? <RecommendationStrip trendSlug={tool.slug} entries={recommendations} /> : null}
        </div>
      ) : null}

      <StickyActionBar
        visible={studioInView && !actionInView}
        meta={m['directory.ai_tools.studio.sticky_meta']({ credits: quality.credits })}
        label={m['directory.ai_tools.studio.generate_short']()}
        busy={generate.isPending}
        onAction={onGenerate}
      />
    </section>
  );
}
